import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { PropertyInteraction } from '../types/match.types';
import { useAuth } from '../context/AuthContext';
import {
  createPropertyInquiry,
  likeProperty as likePropertySecurely,
} from '../services/secureOperations';

interface UsePropertyInteractionProps {
  matchId: string;
  propertyId: string;
  propertyTitle: string;
  partnerUserId?: string;
  partnerName?: string;
}

export const usePropertyInteraction = ({
  matchId,
  propertyId,
  partnerUserId,
}: UsePropertyInteractionProps) => {
  const { currentUser } = useAuth();
  const [interaction, setInteraction] = useState<PropertyInteraction | null>(null);
  const [loading, setLoading] = useState(true);

  // Load interaction data
  useEffect(() => {
    let cancelled = false;

    const loadInteraction = async () => {
      setInteraction(null);
      if (!matchId || !propertyId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const matchRef = doc(firestore, 'matches', matchId);
        const matchDoc = await getDoc(matchRef);

        if (matchDoc.exists()) {
          const data = matchDoc.data() as {
            propertyInteractions?: Record<string, PropertyInteraction>;
          };
          const interactions = data.propertyInteractions ?? {};
          const propertyInteraction = interactions[propertyId];

          if (propertyInteraction) {
            if (!cancelled) setInteraction(propertyInteraction);
          } else {
            if (!cancelled) setInteraction({
              propertyId,
              likedBy: [],
              likedAt: {},
              mutuallyLiked: false,
              savedBy: [],
            });
          }
        }
      } catch (error) {
        console.error('Error loading property interaction:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadInteraction();

    return () => {
      cancelled = true;
    };
  }, [matchId, propertyId]);

  const likeProperty = async () => {
    if (!currentUser || !interaction) return;

    try {
      if (interaction.likedBy.includes(currentUser.uid)) return interaction;
      const result = await likePropertySecurely(matchId, propertyId);
      setInteraction(result.interaction);
      return result.interaction;
    } catch (error) {
      console.error('Error liking property:', error);
      throw error;
    }
  };

  const initiateInquiry = async (message: string) => {
    if (!currentUser || !interaction || !interaction.mutuallyLiked) {
      throw new Error('Both users must like the property before sending an inquiry');
    }

    try {
      const result = await createPropertyInquiry(matchId, propertyId, message);
      const updatedInteraction: PropertyInteraction = {
        ...interaction,
        inquiry: result.inquiry,
      };
      setInteraction(updatedInteraction);
      return updatedInteraction;
    } catch (error) {
      console.error('Error initiating inquiry:', error);
      throw error;
    }
  };

  const confirmInquiry = async () => {
    if (!currentUser || !interaction || !interaction.inquiry) return;

    try {
      return initiateInquiry(interaction.inquiry.message || '');
    } catch (error) {
      console.error('Error confirming inquiry:', error);
      throw error;
    }
  };

  return {
    interaction,
    loading,
    likeProperty,
    initiateInquiry,
    confirmInquiry,
    userHasLiked: interaction?.likedBy.includes(currentUser?.uid || '') || false,
    partnerHasLiked: interaction?.likedBy.includes(partnerUserId || '') || false,
    mutuallyLiked: interaction?.mutuallyLiked || false,
    canSendInquiry: interaction?.mutuallyLiked && !interaction?.inquiry,
    inquiryStatus: interaction?.inquiry?.status,
    pendingConfirmation:
      interaction?.inquiry?.status === 'pending_confirmation' &&
      interaction?.inquiry?.initiatedBy !== currentUser?.uid,
  };
};
